import { useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, InlineStat, StatTile } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { useIncomeExpensesMonthDetail, useCashMovement, useInvoicesSummary, useRevenueVsExpenses } from '../../../hooks/useFinance';
import type { CashMovement, FinanceDataIssue, InvoicesSummary, MonthDetailItem, RevenueVsExpenses, TransferPair } from '../../../api/finance';
import { TileSkeleton } from './TileSkeleton';
import { InfoTip } from '../../../components/ui/InfoTip';
import { Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import {
  money,
  moneyAxis,
  moneyCompact,
  isCurrentMonth,
  formatMonthKey,
  formatDate,
  niceTicks,
  FINANCE_START_MONTH,
  monthsSinceFinanceStart,
  fullMonths,
  monthLabel,
  monthOpacity,
  monthSpan,
  monthTick,
  plusMoney,
  reconcileRounded,
} from './formatters';
import { ChartKey } from './ChartKey';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import { DataIssues, PanelTitle, useFinanceIssues } from './DataIssues';
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_SEMANTIC,
  CHART_SERIES,
  CHART_TOOLTIP,
  RAMP_INK,
  BAR_RADIUS_VERTICAL,
} from '../../../components/ui/chart';

/**
 * In and out are two series, not good and bad, so they take warm neutrals
 * rather than the green/red status colours, and indigo stays for links and
 * focus. The net line is ink, dashed, over the lighter bars it crosses.
 */
const IN = RAMP_INK[2];
const OUT = RAMP_INK[4];
const NET = CHART_SERIES[0];
/** Revenue card: the two revenue readings are lines over light expense bars. */
const MRR = CHART_SERIES[0];
const BOOKED = RAMP_INK[2];
const EXPENSES = RAMP_INK[4];
/** The chart's plot area starts after the Y axis and the left margin; the strip below lines up with it. */
const PLOT_LEFT = 56 + 8;
const PLOT_RIGHT = 8;

const keyItems = (inLabel: string, outLabel: string) => [
  { label: inLabel, color: IN },
  { label: outLabel, color: OUT },
  { label: 'Net (dashed)', color: NET, kind: 'line' as const },
];

/**
 * The months window shared by P&L & Cash and Tool costs, rendered once in the
 * Finance page header. Finance data starts in Jan 2026, so the widest option is
 * "since Jan 26" and shorter ones only appear while they are shorter than that.
 */
export function RangeSelector({ months, onChange }: { months: number; onChange: (m: number) => void }) {
  const sinceStart = monthsSinceFinanceStart();
  // "3M" is three full months, plus the running month to date beside them: the
  // window asks for one month more, so averages (full months only) cover three
  // months and the charts show those three with the month to date marked.
  const options = [
    ...[3, 6, 12]
      .filter((m) => m + 1 < sinceStart)
      .map((m) => ({ id: String(windowForFullMonths(m)), label: `${m}M` })),
    { id: String(sinceStart), label: `Since ${formatMonthKey(FINANCE_START_MONTH)}` },
  ];
  return (
    <SegmentedControl
      ariaLabel="Months shown"
      options={options}
      value={String(Math.min(months, sinceStart))}
      onChange={(id) => onChange(Number(id))}
    />
  );
}

/** The months window behind an "N full months" choice: those N and the running month. */
export function windowForFullMonths(fullMonths: number): number {
  return fullMonths + 1;
}

/** "last 3 full months" or, for the full window, "since Jan 26". */
export function rangeLabel(months: number): string {
  return months >= monthsSinceFinanceStart()
    ? `since ${formatMonthKey(FINANCE_START_MONTH)}`
    : `last ${months - 1} full months`;
}

/**
 * The calendar months a "last N months" window covers, this one included:
 * "Aug–Oct 26, Oct to date". Flows (invoiced, collected) run to today, so the
 * running month is in; averages use full months only and say so with
 * {@link fullMonthsLabel}. Naming both stops "3M" reading as one window.
 */
export function windowMonthsLabel(months: number, now = new Date()): string {
  const keys = Array.from({ length: months }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }).filter((k) => k >= FINANCE_START_MONTH);
  if (keys.length === 0) return rangeLabel(months);
  return `${monthSpan(keys)}, ${formatMonthKey(keys[keys.length - 1]!).split(' ')[0]} to date`;
}

/** "Aug–Sep 26 (2 full months)": the months an average divides by. */
export function fullMonthsLabel(fullMonthKeys: string[]): string {
  const n = fullMonthKeys.length;
  return `${monthSpan(fullMonthKeys)} (${n} full month${n === 1 ? '' : 's'})`;
}

const humanize = (raw: string) => {
  const s = raw.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function MonthDetailList({ title, items, currency }: {
  title: string;
  items: MonthDetailItem[];
  currency: string;
}) {
  return (
    <div>
      <p className="text-body-sm font-medium text-text-secondary mb-1">{title}</p>
      {items.length === 0 ? (
        <p className="text-body-sm text-text-tertiary py-2">None booked this month.</p>
      ) : (
        <div className="divide-y divide-border-light">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 text-body-sm">
              <div className="min-w-0">
                <p className="text-text-primary truncate">{item.companyName}</p>
                <p className="text-body-xs text-text-tertiary">
                  {item.invoiceNumber} · {formatDate(item.accountingDate)}
                  {item.accountingDate !== item.issueDate && ` (dated ${formatDate(item.issueDate)})`}
                  {' · '}
                  {humanize(item.status)}
                </p>
              </div>
              <span className="tabular-nums font-medium flex-shrink-0 ml-3 text-text-primary">
                {money(item.total, currency)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthDetailModal({ month, onClose }: { month: string | null; onClose: () => void }) {
  const { data, isLoading, error, refetch } = useIncomeExpensesMonthDetail(month);

  return (
    <Modal
      isOpen={month !== null}
      onClose={onClose}
      title={month ? `${formatMonthKey(month)}: income and expenses` : ''}
      description={data?.basis}
      size="lg"
    >
      {error ? (
        <ErrorState title="Couldn’t load this month" error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <LoadingState rows={5} />
      ) : data ? (
        <div className="space-y-5">
          <DataIssues issues={data.issues} showWarnings />
          <div className="grid grid-cols-3 gap-4">
            <InlineStat size="sm" label="Income" value={money(data.incomeTotal, data.currency)} />
            <InlineStat size="sm" label="Expenses" value={money(data.expensesTotal, data.currency)} />
            <InlineStat size="sm" label="Net" value={money(data.net, data.currency)} />
          </div>
          <MonthDetailList
            title={`Customer invoices (${data.incomeItems.length})`}
            items={data.incomeItems}
            currency={data.currency}
          />
          <MonthDetailList
            title={`Vendor bills (${data.expenseItems.length})`}
            items={data.expenseItems}
            currency={data.currency}
          />
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * Contracted MRR and booked revenue against booked expenses. MRR is the
 * Overview's number; booked revenue lags it because Odoo releases deferred
 * revenue by journal entries that are posted late. Net is on the MRR basis, so
 * the distance to break-even is what the line shows.
 */
function RevenueVsExpensesCard({
  data,
  isLoading,
  error,
  refetch,
  onSelectMonth,
}: {
  data: RevenueVsExpenses | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  onSelectMonth: (m: string) => void;
}) {
  // The running month is month-to-date on expenses and booked revenue, so its
  // net (MRR − a third of a month's costs) would read as a surplus. Left off the line.
  const chartData = data ? data.series.map((p) => ({ ...p, netLine: p.partial ? null : p.netMrr, bookedLine: p.partial ? null : p.booked })) : [];
  const scale = niceTicks(data ? data.series.flatMap((p) => [p.mrr, p.partial ? 0 : p.booked, p.expenses, p.partial ? 0 : p.netMrr]) : [], 4);
  const selectMonth = (entry: { payload?: { month?: string } }) => {
    if (entry?.payload?.month) onSelectMonth(entry.payload.month);
  };

  return (
    <Card padding="md">
      <PanelTitle
        title="Revenue and expenses"
        info={
          <>
            MRR is contracted monthly recurring revenue, the Overview&apos;s number. Booked revenue and expenses are what
            Odoo posted to income and expense accounts that month, net of VAT. Net is MRR minus expenses, so the gap to
            zero is the distance to break-even. The running month is faded and has no net point: it is only
            month-to-date, so booked revenue and net stop at the last full month. Click a month for the booked entries behind it.
          </>
        }
      />
      {error ? (
        <ErrorState title="Couldn’t load revenue and expenses" error={error} onRetry={() => refetch()} />
      ) : isLoading || !data ? (
        <LoadingState height={CHART_HEIGHT.tall} />
      ) : data.series.every((p) => p.mrr === 0 && p.booked === 0 && p.expenses === 0) ? (
        <EmptyState compact title="No revenue or expense data in this window yet" />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT.tall}>
            <ComposedChart data={chartData} margin={{ top: 8, right: PLOT_RIGHT, left: 8, bottom: 0 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="month" tickFormatter={monthTick} {...CHART_AXIS} />
              <YAxis
                domain={scale.domain}
                ticks={scale.ticks}
                tickFormatter={(v) => moneyAxis(Number(v), data.currency)}
                width={56}
                {...CHART_AXIS}
              />
              <Tooltip
                formatter={(value, name) =>
                  value === null || value === undefined ? ['not shown, month to date', name] : [money(Number(value), data.currency), name]
                }
                labelFormatter={(label) => monthLabel(String(label))}
                {...CHART_TOOLTIP}
              />
              <ReferenceLine y={0} stroke={CHART_SEMANTIC.neutral} />
              <Bar
                dataKey="expenses"
                name="Expenses (booked)"
                fill={EXPENSES}
                radius={[...BAR_RADIUS_VERTICAL]}
                cursor="pointer"
                onClick={selectMonth}
                isAnimationActive={false}
              >
                {data.series.map((p) => <Cell key={p.month} fillOpacity={monthOpacity(p.month)} />)}
              </Bar>
              <Line type="linear" dataKey="mrr" name="MRR" stroke={MRR} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line
                type="linear"
                dataKey="bookedLine"
                name="Booked revenue"
                stroke={BOOKED}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="linear"
                dataKey="netLine"
                name="Net (MRR − expenses)"
                stroke={MRR}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <ChartKey
            items={[
              { label: 'MRR', color: MRR, kind: 'line' },
              { label: 'Booked revenue (full months only)', color: BOOKED, kind: 'line' },
              { label: 'Expenses (booked)', color: EXPENSES },
              { label: 'Net, MRR basis (dashed, full months only)', color: MRR, kind: 'line', opacity: 0.6 },
            ]}
          />
          <NotYetBookedStrip data={data} />
        </>
      )}
    </Card>
  );
}

/**
 * "Not yet booked" per month (MRR minus booked revenue), one cell under each
 * bar of the chart: the grid starts where the plot area starts. The running
 * month gets its cell too, faded and marked MTD. A month whose figure needs
 * explaining (negative booked revenue, or more booked than MRR) lists the
 * entries behind it.
 */
function NotYetBookedStrip({ data }: { data: RevenueVsExpenses }) {
  if (data.series.length === 0) return null;
  return (
    <div className="mt-4 border-t border-border-light pt-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-body-xs text-text-tertiary">
        Not yet booked, MRR minus booked revenue
        <NotYetBookedInfo />
      </p>
      <dl
        className="grid gap-x-1 text-body-xs tabular-nums"
        style={{
          gridTemplateColumns: `repeat(${data.series.length}, minmax(0, 1fr))`,
          paddingLeft: PLOT_LEFT,
          paddingRight: PLOT_RIGHT,
        }}
      >
        {data.series.map((p) => (
          <div key={p.month} className="min-w-0 text-center">
            <dt className="truncate text-text-tertiary">{p.partial ? `${formatMonthKey(p.month)} MTD` : formatMonthKey(p.month)}</dt>
            {p.partial ? (
              // The running month is booked to date against a whole month of MRR: not an exception, not a comparison.
              <dd
                className="truncate text-text-tertiary"
                title={`${money(p.booked, data.currency)} booked so far against ${money(p.mrr, data.currency)} MRR: the month is still being booked`}
              >
                in progress
              </dd>
            ) : (
            <dd className="flex items-center justify-center gap-0.5 text-text-primary">
              {/* Negative means more was booked than MRR (Jan 26: yearly invoices booked in full),
                  so it reads as "booked ahead" rather than as a minus nobody can place. */}
              <span
                className="truncate"
                title={
                  p.notYetBooked < 0
                    ? `${money(-p.notYetBooked, data.currency)} booked ahead of MRR`
                    : money(p.notYetBooked, data.currency)
                }
              >
                {p.mrr === 0 && p.booked === 0
                  ? null
                  : `${p.notYetBooked < 0 ? '−' : ''}${moneyCompact(Math.abs(p.notYetBooked), data.currency)}`}
              </span>
              {p.bookedEntries.length > 0 && <BookedEntriesTip point={p} currency={data.currency} />}
            </dd>
            )}
            {!p.partial && p.notYetBooked < 0 && !(p.mrr === 0 && p.booked === 0) && (
              <dd className="truncate text-text-tertiary">booked ahead</dd>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The entries behind a month whose booked revenue is negative or above MRR. */
function BookedEntriesTip({ point, currency }: { point: RevenueVsExpenses['series'][number]; currency: string }) {
  const negative = point.booked < 0;
  return (
    <InfoTip label={`Why ${formatMonthKey(point.month)} looks odd`}>
      <p>
        {negative
          ? `Booked revenue was ${money(point.booked, currency)} in ${formatMonthKey(point.month)}: credit notes and deferral reversals outweighed what was released.`
          : `${formatMonthKey(point.month)} booked ${money(point.booked, currency)} against ${money(point.mrr, currency)} MRR, so not yet booked is negative: invoices were booked to revenue in full rather than spread over their term.`}
      </p>
      <ul className="mt-2 space-y-0.5 tabular-nums">
        {point.bookedEntries.map((e) => (
          <li key={e.moveName} className="flex justify-between gap-3">
            <span className="min-w-0 break-words">
              {e.moveName} · {e.partner}
            </span>
            <span className="shrink-0 text-text-primary">{money(e.amount, currency)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2">RINV is a credit note; MISC entries are deferral postings; INV an invoice booked to revenue.</p>
    </InfoTip>
  );
}

function NotYetBookedInfo() {
  return (
    <InfoTip label="What not yet booked means">
      Odoo books an annual contract&apos;s invoice to deferred revenue and releases it 1/12 a month with Miscellaneous
      Operations entries. When those entries are posted late, booked revenue falls behind what the contracts carry.
      This is MRR minus booked revenue: positive means revenue the books have not released yet. A month marked
      &ldquo;booked ahead&rdquo; is the opposite: the books released more than MRR that month (a catch-up, or a
      yearly invoice booked in full), and the figure is shown with a minus sign: how much more.
    </InfoTip>
  );
}

function CashMovementCard({ months, burn }: { months: number; burn: BankBurn | null }) {
  const { data, isLoading, error, refetch } = useCashMovement(months);
  const [showPairs, setShowPairs] = useState(false);
  // Fit the axis to the months shown, on round ticks, instead of a symmetric ±range;
  // niceTicks picks the tightest round step, so the outer ticks sit just past the data.
  // The same rule as the revenue chart: the running month's bars are faded and the net line stops at the last full month.
  const cashSeries = data ? data.series.map((p) => ({ ...p, netLine: isCurrentMonth(p.month) ? null : p.net })) : [];
  const scale = niceTicks(
    data ? data.series.flatMap((p) => [p.moneyIn, p.moneyOut, ...(isCurrentMonth(p.month) ? [] : [p.net])]) : [],
    4,
  );

  return (
    <Card padding="md">
      <PanelTitle
        title="Bank cash in and out"
        info={
          <>
            Real money in and out of the bank accounts per month, from Odoo bank statements. The running month is
            faded and has no net point: it is only month-to-date.
            <span className="mt-2 block">
              Transfers between SEQUESTO&apos;s own accounts (KBC, BNP, Revolut, Spendesk top-ups) are not money in
              or out.
              {data && data.excludedTransfersCount > 0
                ? ` In this period ${money(data.excludedTransfersAmount, data.currency)} moved out and ${money(data.excludedTransfersInAmount ?? 0, data.currency)} moved in between them (${data.excludedTransfersCount} statement lines), and none of it is counted.`
                : ' None were found in this period.'}
            </span>
            <span className="mt-2 block">
              A payment that came back (rejected by the payee&apos;s bank, or refunded because it was sent by mistake)
              cancels the original: it is not money in, and the original is taken off money out in its month.
            </span>
          </>
        }
      />
      {error ? (
        <ErrorState title="Couldn’t load cash movement" error={error} onRetry={() => refetch()} />
      ) : isLoading || !data ? (
        <LoadingState height={CHART_HEIGHT.tall} />
      ) : data.series.every((p) => p.moneyIn === 0 && p.moneyOut === 0) ? (
        <EmptyState
          compact
          title="No bank statement data in this window yet"
          description="Trigger an Odoo sync to load it."
        />
      ) : (
        <>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT.tall}>
          <ComposedChart data={cashSeries} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid {...CHART_GRID} />
            <XAxis dataKey="month" tickFormatter={monthTick} {...CHART_AXIS} />
            <YAxis
              domain={scale.domain}
              ticks={scale.ticks}
              tickFormatter={(v) => moneyAxis(Number(v), data.currency)}
              width={56}
              {...CHART_AXIS}
            />
            <Tooltip
              formatter={(value, name) =>
                value === null || value === undefined ? ['not shown, month to date', name] : [money(Number(value), data.currency), name]
              }
              labelFormatter={(label) => monthLabel(String(label))}
              {...CHART_TOOLTIP}
            />
            <Bar dataKey="moneyIn" name="Money in" fill={IN} radius={[...BAR_RADIUS_VERTICAL]} isAnimationActive={false}>
              {data.series.map((p) => <Cell key={p.month} fillOpacity={monthOpacity(p.month)} />)}
            </Bar>
            <Bar dataKey="moneyOut" name="Money out" fill={OUT} radius={[...BAR_RADIUS_VERTICAL]} isAnimationActive={false}>
              {data.series.map((p) => <Cell key={p.month} fillOpacity={monthOpacity(p.month)} />)}
            </Bar>
            <Line type="linear" dataKey="netLine" name="Net" stroke={NET} strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <ChartKey items={keyItems('Money in', 'Money out')} />
        {burn && (
          <p className="mt-3 text-body-xs text-text-tertiary">
            {burn.value < 0 ? 'Average net cash in' : 'Average monthly cash burn'}{' '}
            <span className="font-medium text-text-primary">{money(Math.abs(burn.value), burn.currency)}</span> over{' '}
            {burn.span}, internal transfers left out.
          </p>
        )}
        {data.returnedPayments && data.returnedPayments.count > 0 && (
          <p className="mt-2 text-body-xs text-text-tertiary">
            {data.returnedPayments.count === 1 ? 'One payment came back' : `${data.returnedPayments.count} payments came back`} (
            {money(data.returnedPayments.amount, data.currency)}) and{' '}
            {data.returnedPayments.count === 1 ? 'is' : 'are'} netted against the payment{' '}
            {data.returnedPayments.count === 1 ? 'it' : 'they'} reversed:{' '}
            {[...new Map(
              data.returnedPayments.lines
                .filter((l) => l.originalLineId)
                .map((l) => [l.originalLineId, l]),
            ).values()]
              .map((l) => {
                const sum = data.returnedPayments!.lines
                  .filter((x) => x.originalLineId === l.originalLineId)
                  .reduce((s, x) => s + x.amount, 0);
                return `${money(sum, data.currency)} of the ${formatDayMonthYear(l.originalDate!)} payment on ${l.journalName ?? 'the account'}`;
              })
              .join('; ')}
            .
          </p>
        )}
        <TransferChecks data={data} onOpen={() => setShowPairs(true)} />
        </>
      )}
      {/* Whether a €0 is real: a stalled journal in place as an error, the "statements are in" note as a caption. */}
      {data && <DataIssues issues={data.issues?.filter((i) => i.severity === 'error')} />}
      {data?.issues
        ?.filter((i) => i.severity !== 'error')
        .map((i) => (
          <p key={i.code + i.message} className="mt-2 text-body-xs text-text-tertiary">
            {i.message}
          </p>
        ))}
      {data && (
        <Modal
          isOpen={showPairs}
          onClose={() => setShowPairs(false)}
          title="Transfers matched automatically"
          description="Money that left one of our bank accounts and arrived on another, unreconciled in Odoo. Both halves are left out of money in and money out, like the transfers Odoo reconciled."
          size="lg"
        >
          <TransferTable pairs={data.autoMatchedTransfers.pairs} />
        </Modal>
      )}
    </Card>
  );
}

/** "3 transfers auto-matched (€42,000) →" and the near-misses that are still counted. */
function TransferChecks({ data, onOpen }: { data: CashMovement; onOpen: () => void }) {
  const { count, amount } = data.autoMatchedTransfers;
  const possible = data.possibleTransfers;
  return (
    <div className="mt-4 space-y-2 border-t border-border-light pt-3 text-body-xs">
      {count > 0 ? (
        <button
          type="button"
          onClick={onOpen}
          className="font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded-sm"
        >
          {count} transfer{count === 1 ? '' : 's'} between own accounts auto-matched ({money(amount, data.currency)}) →
        </button>
      ) : (
        <p className="text-text-tertiary">
          No unreconciled transfers between own accounts in this window: every one Odoo has is already reconciled.
        </p>
      )}
      {possible.length > 0 && (
        <div>
          <p className="text-text-secondary">Possible transfers (still counted)</p>
          <ul className="mt-1 divide-y divide-border-light">
            {possible.slice(0, 5).map((p) => (
              <li key={`${p.outLineId}-${p.inLineId}`} className="flex flex-wrap justify-between gap-x-3 py-1.5 tabular-nums">
                <span className="text-text-secondary">
                  {formatDate(p.outDate)} · {p.fromJournal} → {p.toJournal}
                  <span className="text-text-tertiary"> · {p.doubt}</span>
                </span>
                <span className="text-text-primary">{money(p.amount, p.currency)}</span>
              </li>
            ))}
          </ul>
          {possible.length > 5 && <p className="text-text-tertiary">and {possible.length - 5} more</p>}
        </div>
      )}
    </div>
  );
}

function TransferTable({ pairs }: { pairs: TransferPair[] }) {
  const pagination = usePagination(pairs);
  return (
    <>
      <Table colWidths={['22%', '40%', '18%', '20%']}>
        <THead>
          <tr>
            <Th>Date</Th>
            <Th>From → to</Th>
            <Th align="right">Amount</Th>
            <Th>Evidence</Th>
          </tr>
        </THead>
        <tbody>
          {pagination.pageItems.map((p) => (
            <Tr key={`${p.outLineId}-${p.inLineId}`}>
              <Td className="tabular-nums">
                {formatDate(p.outDate)}
                {p.inDate !== p.outDate && <span className="block text-body-xs text-text-tertiary">in {formatDate(p.inDate)}</span>}
              </Td>
              <Td>
                {p.fromJournal} → {p.toJournal}
              </Td>
              <Td align="right" className="tabular-nums">
                {money(p.amount, p.currency)}
                {p.currencyIn !== p.currency && (
                  <span className="block text-body-xs text-text-tertiary">{money(p.amountIn, p.currencyIn)} in</span>
                )}
              </Td>
              <Td className="text-body-xs text-text-tertiary">{p.evidence.length ? p.evidence.join(', ') : 'amount and date'}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination
        page={pagination.page}
        pageCount={pagination.pageCount}
        total={pagination.total}
        pageSize={pagination.pageSize}
        onPage={pagination.setPage}
      />
    </>
  );
}

/** Invoiced vs collected, with credit notes disclosed rather than netted. */
function InvoicingCard({ months }: { months: number }) {
  // The tiles' window: full months only, so this card and the tiles above
  // cover the same months ('all' is only the fallback the months override).
  const { data, isLoading, error, refetch } = useInvoicesSummary('all', months, true);
  const cash = useCashMovement(months);
  const s = data?.summary;
  useFinanceIssues('pnl.invoicing', s?.issues);
  const periodMonths = s?.periodStart ? monthKeysBetween(s.periodStart, s.periodEnd ?? null) : [];
  // While loading, the same full months the API will answer with, so the title does not change under the reader.
  const now = new Date();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const expectedMonths = monthKeysBetween(
    iso(new Date(now.getFullYear(), now.getMonth() - months + 1, 1)),
    iso(new Date(now.getFullYear(), now.getMonth(), 1)),
  );
  const shownMonths = periodMonths.length > 0 ? periodMonths : expectedMonths;
  const title = shownMonths.length > 0 ? fullMonthsLabel(shownMonths) : windowMonthsLabel(months);

  return (
    <Card padding="md">
      <PanelTitle
        title={`Invoiced and paid, ${title}`}
        info="Customer invoices issued in the full months of the period (the same months as the tiles above; the month to date is left out) and what has been paid on them since. A credit note is shown on its own, not netted against the invoiced total. Invoices dated after today are not counted: they are scheduled, not issued."
      />
      {error ? (
        <ErrorState title="Couldn’t load invoicing" error={error} onRetry={() => refetch()} />
      ) : isLoading || !s ? (
        <LoadingState rows={2} />
      ) : (
        <>
          <DataIssues issues={s.issues} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <InlineStat
              size="sm"
              label="Invoiced"
              value={money(s.invoicedTotal, s.currency)}
              sub={
                s.scheduledInvoices.length > 0
                  ? `+ ${money(s.scheduledTotal, s.currency)} scheduled on ${s.scheduledInvoices.length} future-dated invoice${s.scheduledInvoices.length === 1 ? '' : 's'}`
                  : undefined
              }
            />
            <InlineStat
              size="sm"
              label="Paid of these invoices"
              value={money(s.collectedTotal, s.currency)}
              sub="paid to date, on invoices issued in the period"
            />
            <InlineStat
              size="sm"
              label="Outstanding today"
              value={money(s.outstandingTotal, s.currency)}
              sub={`${money(s.outstandingTotal - s.overdueTotal, s.currency)} not yet due · ${money(s.overdueTotal, s.currency)} overdue`}
            />
            <InlineStat
              size="sm"
              label="Credit notes"
              value={money(s.creditNotesTotal, s.currency)}
              sub={
                Math.abs(s.reversedTotal) >= 0.005
                  ? `${money(s.reversedTotal, s.currency)} of invoices cancelled by credit notes`
                  : undefined
              }
            />
          </div>
          <InvoicingBridge s={s} />
          <BankBridge s={s} cash={cash.data} cashError={cash.error} months={periodMonths} />
        </>
      )}
    </Card>
  );
}

/**
 * What the credit notes that cleared no invoice of the period did, from what
 * the API read in Odoo's payment list: paid back by bank (Eltel RINV/2026/00001
 * on 2 Mar, the refund of a double invoice) or set against an invoice from
 * before the period. Older API builds do not say, and the words stay general.
 */
export function otherCreditWords(notes: InvoicesSummary['creditNotes']): string {
  const kinds = new Set(notes.map((c) => c.settledBy ?? 'unknown'));
  if (kinds.size === 1 && kinds.has('refund')) return 'refunded to the customer by bank';
  if (kinds.size === 1 && kinds.has('invoice')) return 'set against invoices from before the period';
  if (kinds.size === 1 && kinds.has('netted')) return 'set off inside customer payments';
  if (!kinds.has('unknown')) return 'refunded by bank, set off inside a payment, or set against an earlier invoice';
  return 'settled outside the period’s invoices, by a refund or against an earlier invoice';
}

/** ", refunded 2 Mar 2026" / ", against INV/2025/00063" for one credit note, or nothing when the API does not say. */
export function creditNoteFate(c: InvoicesSummary['creditNotes'][number]): string {
  if (c.settledBy === 'refund') return c.settledOn ? `, refunded ${formatDate(c.settledOn)}` : ', refunded';
  if (c.settledBy === 'invoice' && c.against) return `, against ${c.against}`;
  if (c.settledBy === 'netted') return c.settledOn ? `, set off in a payment ${formatDate(c.settledOn)}` : ', set off in a payment';
  return '';
}

/**
 * How the four figures fit together: what was invoiced in the period went
 * either to cash, to a credit note, or is still open; credit notes take the
 * gross down to the net Invoiced figure. Outstanding is the receivables
 * position today, across every period, so it is said separately.
 */
function InvoicingBridge({ s }: { s: InvoicesSummary }) {
  const unsynced = s.grossInvoicedTotal - s.collectedTotal - s.reversedTotal - s.openOnPeriodTotal;
  const other = s.creditNotes.filter((c) => c.clears === null && c.residual >= -0.005);
  // Rounded so each sentence adds up to the euro as printed. The parts keep the
  // figures the tiles show; the gross total, shown only here, takes the cents.
  const [gross, collected, reversed, open, rest] = reconcileRounded(
    s.grossInvoicedTotal,
    [s.collectedTotal, s.reversedTotal, s.openOnPeriodTotal, unsynced],
    'total',
  );
  // The credit-note total is a tile; the last part this sentence prints absorbs the cents.
  // Said as amounts credited (positive), so the sentence never reads "+ −€30,000".
  const creditParts = [s.reversedTotal, -s.creditUnappliedTotal, -s.creditOtherTotal];
  const lastShown = Math.abs(s.creditOtherTotal) >= 0.5 ? 2 : Math.abs(s.creditUnappliedTotal) >= 0.5 ? 1 : 0;
  const [credits, cleared, unapplied, otherCredit] = reconcileRounded(-s.creditNotesTotal, creditParts, lastShown);
  // A part that rounds to nothing is left out rather than printed as "€0 that cleared those invoices".
  const creditTerms = [
    { amount: cleared!, words: 'that cleared those invoices' },
    ...(Math.abs(s.creditUnappliedTotal) >= 0.5
      ? [{ amount: unapplied!, words: 'credit not used yet (already taken off outstanding)' }]
      : []),
    ...(Math.abs(s.creditOtherTotal) >= 0.5
      ? [{ amount: otherCredit!, words: otherCreditWords(other) }]
      : []),
  ].filter((t, _i, all) => t.amount !== 0 || all.length === 1);
  return (
    <div className="mt-4 space-y-1 border-t border-border-light pt-3 text-body-xs leading-relaxed text-text-tertiary tabular-nums">
      <p>
        {money(gross, s.currency)} invoiced = {money(collected!, s.currency)} paid{' '}
        {/* A part that rounds to nothing is left out, not printed as "+ €0 cleared". */}
        {reversed !== 0 && `${plusMoney(reversed!, s.currency)} cleared by credit notes `}
        {plusMoney(open!, s.currency)} still open
        {Math.abs(unsynced) >= 0.5 && ` ${plusMoney(rest!, s.currency)} with no open balance synced yet`}.
      </p>
      {Math.abs(s.creditNotesTotal) >= 0.5 && (
        <p>
          Credit notes {money(credits, s.currency)} ={' '}
          {creditTerms
            .map((t, i) => `${i === 0 ? money(t.amount, s.currency) : plusMoney(t.amount, s.currency)} ${t.words}`)
            .join(' ')}
          {Math.abs(s.creditOtherTotal) >= 0.5 &&
            other.length > 0 &&
            ` (${other.map((c) => `${c.invoiceNumber} ${c.companyName} ${money(Math.abs(c.total), s.currency)}${creditNoteFate(c)}`).join(', ')})`}
          . Net invoiced {money(s.invoicedTotal, s.currency)}.
        </p>
      )}
      <p>Outstanding today ({money(s.outstandingTotal, s.currency)}) covers invoices from every period, not only this one.</p>
    </div>
  );
}

/** "2026-07-01", "2026-10-01" → ["2026-07", "2026-08", "2026-09"]; open end runs to this month. */
export function monthKeysBetween(startIso: string, endIsoExclusive: string | null, now = new Date()): string[] {
  const end = endIsoExclusive
    ? endIsoExclusive.slice(0, 7)
    : `${now.getFullYear()}-${String(now.getMonth() + 2).padStart(2, '0')}`;
  const keys: string[] = [];
  let [y, m] = startIso.slice(0, 7).split('-').map(Number) as [number, number];
  for (let guard = 0; guard < 48; guard++) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (key >= end) break;
    keys.push(key);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return keys;
}

/**
 * Why "Paid of these invoices" is far below the bank chart's money in: most
 * cash in a quarter pays invoices from before it (yearly licences billed in
 * spring), and some is not a customer payment at all (a VAT refund).
 */
function BankBridge({
  s,
  cash,
  cashError,
  months,
}: {
  s: InvoicesSummary;
  cash: CashMovement | undefined;
  cashError: unknown;
  months: string[];
}) {
  if (!s.paidInPeriod || months.length === 0) return null;
  const span = monthSpan(months);
  if (cashError) {
    return <p className="mt-1 text-body-xs text-text-tertiary">Bank money in for {span} is unavailable, so it can’t be set beside these invoices.</p>;
  }
  if (!cash) return null;
  const inMonths = new Set(months);
  const bankIn = cash.series.filter((p) => inMonths.has(p.month)).reduce((sum, p) => sum + p.moneyIn, 0);
  const { onPeriodInvoices, onEarlierInvoices } = s.paidInPeriod;
  const [total, onThese, onEarlier, other] = reconcileRounded(
    bankIn,
    [onPeriodInvoices, onEarlierInvoices, bankIn - onPeriodInvoices - onEarlierInvoices],
    2,
  );
  const later = s.collectedTotal - onPeriodInvoices;
  const notCustomer = notCustomerItems(cash, months);
  return (
    <div className="mt-1 space-y-1 text-body-xs leading-relaxed text-text-tertiary tabular-nums">
      <p>
        Bank money in, {span} ({money(total, s.currency)}, the cash chart above) = {money(onThese!, s.currency)} paid on
        these invoices {plusMoney(onEarlier!, s.currency)} paid on invoices from before {formatMonthKey(months[0]!)}{' '}
        {plusMoney(other!, s.currency)} not paid against a customer invoice in Odoo
        {notCustomer && notCustomer.items.length > 0
          ? `, the largest: ${notCustomer.items.map(describeInflow).join('; ')}`
          : ''}
        .
        {Math.abs(later) >= 0.5 && ` ${money(later, s.currency)} of these invoices was paid after ${formatMonthKey(months[months.length - 1]!)}.`}
      </p>
      {/* A negative remainder is impossible as money: say it, never print it as if it were a figure. */}
      {other! < 0 && (
        <p className="text-warning-dark">
          The invoices’ payment records add up to {money(-other!, s.currency)} more than the bank received in {span}: some of
          what Odoo lists as paid did not arrive as bank money in these months (a payment set off against a credit note, or
          a payment dated outside the statement it was matched to). The bridge does not close until that is found.
        </p>
      )}
    </div>
  );
}

/** "€194,000 BNP Current Account, 12 Jun (Loan repayment returned)" for one inflow. */
function describeInflow(line: NonNullable<CashMovement['notCustomerIn']>['lines'][number]): string {
  const who = line.partnerName ?? line.journal ?? 'bank';
  const why = line.paymentRef ? ` (${line.paymentRef.length > 60 ? `${line.paymentRef.slice(0, 57)}…` : line.paymentRef})` : '';
  const unmatched = line.counterpartAccountCode === null ? ', not reconciled yet' : '';
  return `${money(line.amount, 'EUR')} ${who}, ${formatDate(line.date)}${why}${unmatched}`;
}

/** The three biggest inflows of the months that were not customer payments, when the API sends them. */
function notCustomerItems(cash: CashMovement, months: string[]) {
  if (!cash.notCustomerIn) return null;
  const inMonths = new Set(months);
  const items = cash.notCustomerIn.lines.filter((l) => inMonths.has(l.date.slice(0, 7))).slice(0, 3);
  return { items };
}

interface BankBurn {
  value: number;
  span: string;
  currency: string;
}

/**
 * Average monthly burn from the bank statements: money out minus money in,
 * over the full months of the page's period. The running month is left out
 * (month-to-date). Same query as the cash card, so one request serves both.
 */
function useMonthlyBurn(months: number) {
  const { data, isLoading, error } = useCashMovement(months);
  if (!data) return { isLoading, error, burn: null as BankBurn | null };
  const full = fullMonths(
    data.series.map((p) => ({ ...p, total: p.moneyIn + p.moneyOut })),
    null,
  );
  if (full.length === 0) return { isLoading, error, burn: null };
  const value = full.reduce((sum, p) => sum + (p.moneyOut - p.moneyIn), 0) / full.length;
  return { isLoading, error, burn: { value, span: fullMonthsLabel(full.map((p) => p.month)), currency: data.currency } };
}

/** "€94,445" or, when revenue covers costs, "€12,000 surplus". */
function burnValue(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '—';
  return value < 0 ? `${money(-value, currency)} surplus` : money(value, currency);
}

export function IncomeExpensesTab({ months }: { months: number }) {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const revenue = useRevenueVsExpenses(months);
  const data = revenue.data;
  const burn = useMonthlyBurn(months);

  const currency = data?.currency ?? 'EUR';
  const sum = data?.summary;
  const span = sum && sum.fullMonths.length > 0 ? fullMonthsLabel(sum.fullMonths) : null;

  // No expenses at all means the ledger sync wrote nothing on that side, and
  // every burn and break-even figure would be fiction.
  const noBills: FinanceDataIssue[] =
    data && data.series.some((p) => p.booked !== 0) && data.series.every((p) => p.expenses === 0)
      ? [{
          code: 'no_vendor_bills',
          severity: 'warning',
          message: 'No expenses have been synced, so expenses show as zero. Check the Odoo connection and trigger a sync.',
        }]
      : [];
  useFinanceIssues('pnl.income', data ? [...data.issues, ...noBills] : undefined);

  // One MRR for the whole page: today's, the Overview's number (the newest series point).
  const newest = data?.series[data.series.length - 1];
  const currentMrr = sum?.currentMrr ?? newest?.mrr ?? null;
  const gap =
    sum && currentMrr !== null && sum.avgExpenses !== null ? Math.max(0, sum.avgExpenses - currentMrr) : (sum?.gapToBreakEven ?? null);
  const trend = sum?.mrrTrendPerMonth ?? null;
  const trendText = trend === null ? null : `MRR trend ${trend >= 0 ? '+' : '−'}${money(Math.abs(trend), currency)}/mo`;
  const bankBurn = burn.burn;
  const tilesLoading = revenue.isLoading || (burn.isLoading && !bankBurn);

  return (
    <div className="space-y-6">
      <DataIssues issues={data?.issues} />

      {/* Every tile reads the period picked in the page header, full months only. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tilesLoading ? (
          ['Avg monthly burn', 'Avg monthly expenses', 'Gap to break-even', 'Not yet booked'].map((label) => (
            <TileSkeleton key={label} label={label} />
          ))
        ) : (
          <>
            <StatTile
              label="Avg monthly burn"
              value={bankBurn ? burnValue(bankBurn.value, bankBurn.currency) : burn.error ? 'Unavailable' : '—'}
              sub={bankBurn ? `${bankBurn.span} · bank statements, money out − in` : 'No full month in this period yet'}
              icon={
                <InfoTip label="How burn is measured">
                  <p>
                    Cash that left the bank accounts minus cash that came in, averaged over the full months of the period:{' '}
                    {bankBurn?.span ?? 'none yet'}. Internal transfers between own accounts are left out.
                  </p>
                  {sum && (
                    <p className="mt-2">
                      On the ledger instead: booked expenses − MRR is {burnValue(sum.burnMrr, currency)} a month (MRR
                      basis), and booked expenses − booked revenue is {burnValue(sum.burnBooked, currency)} (booked
                      basis, higher while revenue entries lag; see Not yet booked){span ? `, both over ${span}` : ''}.
                    </p>
                  )}
                </InfoTip>
              }
            />
            <StatTile
              label="Avg monthly expenses"
              value={sum ? (sum.avgExpenses === null ? '—' : money(sum.avgExpenses, currency)) : 'Unavailable'}
              sub={span ? `${span} · booked in Odoo, net of VAT` : 'No full month in this period yet'}
            />
            <StatTile
              label="Gap to break-even"
              value={
                !sum
                  ? 'Unavailable'
                  : gap === null
                    ? '—'
                    : gap === 0
                      ? 'Reached'
                      : `${money(gap, currency)}/mo`
              }
              sub={
                currentMrr !== null
                  ? `vs current MRR ${money(currentMrr, currency)} · avg expenses ${span ?? ''}`
                  : undefined
              }
              tooltip={
                currentMrr !== null && sum && sum.avgExpenses !== null
                  ? `Average monthly booked expenses (${money(sum.avgExpenses, currency)}, ${span}) minus current MRR (${money(currentMrr, currency)}, the Overview number): how much more MRR a month it takes to cover costs.${trendText ? ` ${trendText} over the same months.` : ''}`
                  : undefined
              }
            />
            <StatTile
              label="Not yet booked"
              value={sum ? (sum.notYetBookedTotal === null ? '—' : money(sum.notYetBookedTotal, currency)) : 'Unavailable'}
              sub={span ? `${span} · MRR − booked revenue` : undefined}
              icon={<NotYetBookedInfo />}
            />
          </>
        )}
      </div>

      <RevenueVsExpensesCard
        data={data}
        isLoading={revenue.isLoading}
        error={revenue.error}
        refetch={() => revenue.refetch()}
        onSelectMonth={setSelectedMonth}
      />
      <CashMovementCard months={months} burn={bankBurn} />
      <InvoicingCard months={months} />

      <MonthDetailModal month={selectedMonth} onClose={() => setSelectedMonth(null)} />
    </div>
  );
}
